import ENUMS from '../constants/card-brand-type-enums';
import * as CARD_ERROR from '../constants/card-error-enums';
import {
  BRAZIL_COUNTRY_CODE,
  CANADA_COUNTRY_CODE,
  DEBIT,
  ELECTRON,
  NOT_VERIFIED,
  VISA
} from '../constants/common-enums';
import getCardInfo from './get-card-info';
import isEmptyObject from './is-empty-object';
import isMoreAddressInfoRequired from './is-more-address-info-required';

/**
 * Return card error enums based on the business defined card/merchant error rules.
 *
 * - Generic Card Errors Rules:-
 *    - returns `card_expired` if card expired flag is true
 *    - returns `card_not_verified` if card verificationStatus has NOT_VERIFIED
 *    - returns `unsupported_billing_address` when card billingAddress --> billingAccepted flag is false
 *    - TBD rule for `partial_billing_address`
 * - Merchant Specific Rules:-
 *   - returns `merchant_unsupported_card_brand` when the cardBrand is not in the
 *     merchantConfig --> paymentConstraints --> supported card brand array.
 *   - returns `unsupported_canadian_card` when
 *     merchantConfig -> paymentConstraints --> acceptCanadianVISADebit is set to false
 * - If a card has multiple errors, then per requirement we will rely on priority order
 *   1. Card brand not accept
 *   2. Unsupported Billing address
 *   3. Card Expired
 *   4. Card not verified
 *   5. Partial billing address
 * - combinationCard used to identify BR Combo card has possible values as below
 *   - A = credit/debit combo card
 *   - C = credit/prepaid combo card
 *   - N = not a combo card (is BR Visa electron)
 *   - <null> = not a combo card
 *
 * @param {object} data
 * @returns {string} error enum
 */
const getCardError = data => {
  const {
    card,
    isComboCard,
    merchantBillingAddressConstraints,
    merchantCanadaVisaDebitSupport,
    merchantCountryCode,
    merchantSupportedBillingCountries,
    merchantSupportedCardBrands
  } = data;
  let cardError = null;
  if (!isEmptyObject(card)) {
    const {
      billingCountryCode,
      cardBrand,
      cardType,
      fullBillingAddress,
      isBillingAddressAccepted,
      isExpired,
      isIssuerManaged,
      verificationStatus
    } = getCardInfo(card);
    const filterBrands = (merchantSupportedCardBrands, merchantCountryCode) => {
      if (merchantCountryCode === BRAZIL_COUNTRY_CODE) {
        return merchantSupportedCardBrands;
      }
      return merchantSupportedCardBrands.filter(cardBrand => cardBrand !== ELECTRON);
    };
    const isCardNotVerified = verificationStatus === NOT_VERIFIED;
    const isCaVisaDebitCard =
      billingCountryCode === CANADA_COUNTRY_CODE &&
      cardBrand === VISA &&
      (cardType && cardType.toLowerCase() === DEBIT);

    /**
     * Validate card brand supported cases:
     * General: 1. User's card brand exists in merchantAcceptedCardBrands array.
     * Special: 2. When merchantAcceptedCardBrands array is empty -> accept all brands.
     *          3. BR merchant accepts Electron only, user holds combo card.
     * @param {string} cardBrand
     * @param {boolean} isComboCard
     * @param {string} merchantCountryCode
     * @param {array} merchantSupportedCardBrands
     * @returns {boolean} true for invaild brand false for valid brand
     */
    const validateCardBrand = (
      cardBrand,
      isComboCard,
      merchantCountryCode,
      merchantSupportedCardBrands
    ) => {
      const filteredSupportedCardBrands = filterBrands(
        merchantSupportedCardBrands,
        merchantCountryCode
      );
      const noBrandsSupported =
        merchantCountryCode !== BRAZIL_COUNTRY_CODE &&
        merchantSupportedCardBrands.length === 1 &&
        merchantSupportedCardBrands[0] === ELECTRON;
      const allBrandsSupported = merchantSupportedCardBrands.length === 0;
      const isComboCardSupported = isComboCard && filteredSupportedCardBrands.includes(ELECTRON);
      return (
        noBrandsSupported ||
        (!allBrandsSupported &&
          !(filteredSupportedCardBrands.includes(cardBrand) || isComboCardSupported))
      );
    };
    // based on RXO behavior, when non-BR merchant doesn't support VISA or BR merchant doesn't support Visa and Electron, card is combo card. Error message contains card type.
    const noBrandsSupported =
      merchantCountryCode !== BRAZIL_COUNTRY_CODE &&
      merchantSupportedCardBrands.length === 1 &&
      merchantSupportedCardBrands[0] === ELECTRON;
    const allBrandsSupported = merchantSupportedCardBrands.length === 0;
    /**
     * ELO card brand error
     */
    let isEloCardInvalid = false;
    // cardBrand needs to be ELO first
    if (cardBrand === ENUMS.CARD_BRANDS.ELO) {
      // merchant in non-BR
      if (merchantCountryCode !== BRAZIL_COUNTRY_CODE) {
        if (!merchantSupportedCardBrands.includes(ENUMS.CARD_BRANDS.ELO)) {
          isEloCardInvalid = true;
        }
      } else if (
        !allBrandsSupported &&
        !merchantSupportedCardBrands.includes(ENUMS.CARD_BRANDS.ELO)
      ) {
        // merchant is BR
        isEloCardInvalid = true;
      }
    }

    const comboCardError =
      isComboCard &&
      (noBrandsSupported ||
        (!allBrandsSupported &&
          !merchantSupportedCardBrands.includes(VISA) &&
          (merchantCountryCode !== BRAZIL_COUNTRY_CODE ||
            !merchantSupportedCardBrands.includes(ELECTRON))));

    const isInvalidCardBrand = validateCardBrand(
      cardBrand,
      isComboCard,
      merchantCountryCode,
      merchantSupportedCardBrands
    );
    const inValidBillingCountry =
      merchantSupportedBillingCountries.length > 0 &&
      !merchantSupportedBillingCountries.includes(billingCountryCode);
    /** partial billing address start */
    // START GENAI@GHCOPILOT
    // if card is issuer managed, then ignore partial address error
    const hasPartialAddressError =
      !isIssuerManaged &&
      isMoreAddressInfoRequired(fullBillingAddress, merchantBillingAddressConstraints);
    // END GENAI@GHCOPILOT
    /** partial billing address end */

    /** DO NOT CHANGE THE ORDER of If.Else blocks. This follows a rule for multiple errors in a card. */

    /**
     * TODO: Refactor invalid brand validation POST October Release
     * Requirement changes frequently -> need to change code style to make it easier to maintain
     */
    if (isEloCardInvalid) {
      cardError = CARD_ERROR.MERCHANT_UNSUPPORTED_CARD_BRAND;
    } else if (comboCardError) {
      cardError = CARD_ERROR.MERCHANT_UNSUPPORTED_CARD_BRAND_TYPE;
    } else if (isInvalidCardBrand) {
      cardError = CARD_ERROR.MERCHANT_UNSUPPORTED_CARD_BRAND;
    } else if (!merchantCanadaVisaDebitSupport && isCaVisaDebitCard) {
      // merchantCanadaVisaDebitSupport defaulted to true in get-card-list since paymentConstraints are optional in merchantConfig.
      // Followed above condition as in RXO
      cardError = CARD_ERROR.UNSUPPORTED_CANADIAN_CARD;
    } else if (!isBillingAddressAccepted || inValidBillingCountry) {
      cardError = CARD_ERROR.UNSUPPORTED_BILLING_ADRESS;
    } else if (isExpired) {
      cardError = CARD_ERROR.CARD_EXPIRED;
    } else if (isCardNotVerified) {
      cardError = CARD_ERROR.CARD_NOT_VERIFIED;
    } else if (hasPartialAddressError) {
      cardError = CARD_ERROR.PARTIAL_BILLING_ADDRESS;
    }
  }
  return cardError;
};

export default getCardError;
